import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import arrowLeft from '@/assets/images/arrow-left.svg';
import chevronBack from '@/assets/images/chevronBack.svg';
import star from '@/assets/images/star.svg';
import { LanguageSwitcher } from '@/components/LanguageSwitcher/LanguageSwitcher';
import { useBranchMenus } from '@/hooks/useBranchMenus';
import { useLanguage } from '@/hooks/useLanguage';
import { useLocale } from '@/hooks/useLocale';
import { isValidLogoUrl } from '@/utils/isValidLogoUrl';
import { buildUrlWithParams } from '@/utils/queryParams';
import { roundRatingsNumber } from '@/utils/roundRatingsNumber';
import ViewSwitcher from '../ViewSwitcher/VIewSwitcher';
import styles from './BrandHeader.module.scss';
import { HeaderLogo } from './HeaderLogo';
import { SearchInput } from './SearchInput';

interface Props {
  brandId: string;
  kitchenId: string;
  pointOfSaleId: string;
  outletId: string;
  tableNumber?: string;
  counterNumber?: string;
  origin?: string;
  onSearch: (searchTerm: string) => void;
}

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

export const MIN_SEARCH_LENGTH = 2;

export const BrandHeader = ({
  brandId,
  kitchenId,
  pointOfSaleId,
  outletId,
  tableNumber,
  counterNumber,
  origin,
  onSearch,
}: Props) => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { data: branchMenus } = useBranchMenus({ brandId, kitchenId, pointOfSaleId, locale });
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isDescriptionShowed, setIsDescriptionShowed] = useState(false);

  const isCollapsed = searchTerm.length > MIN_SEARCH_LENGTH;

  const searchPlaceholder = t('brandHeader.search');

  useEffect(() => {
    onSearch(debouncedSearchTerm.length > MIN_SEARCH_LENGTH ? debouncedSearchTerm : '');
  }, [debouncedSearchTerm, onSearch]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const clearSearch = () => {
    setSearchTerm('');
    onSearch('');
  };

  const handleBackClick = () => {
    const baseUrl = origin || '/';

    const backUrl = buildUrlWithParams(baseUrl, {
      outletId,
      brandId,
      tableNumber: tableNumber !== 'undefined' ? tableNumber : undefined,
      counterNumber: counterNumber !== 'undefined' ? counterNumber : undefined,
    });

    navigate(backUrl);
  };

  if (!branchMenus) {
    return null;
  }

  const { brand } = branchMenus;
  const hasOrderReference = tableNumber || counterNumber || origin;

  return (
    <div className={`${styles.root} ${isCollapsed ? styles.collapsed : ''} ${isRTL && styles.rtl}`}>
      <div className={`${styles.backgroundImage} ${isCollapsed ? styles.hidden : ''}`}>
        {brand.imageUris && (
          <img
            src={brand.imageUris['720_720']}
            alt={brand.name}
            className={styles.backgroundImg}
            data-testid="brand-bg-image"
          />
        )}
        <div className={styles.overlay}></div>
      </div>

      {isCollapsed && (
        <div
          className={`${styles.collapsedHeader} ${isRTL && styles.rtl}`}
          data-testid="brand-collapsed-header"
        >
          {hasOrderReference && (
            <button
              className={styles.collapsedBackButton}
              onClick={clearSearch}
              aria-label={t('brandHeader.goBack')}
              data-testid="brand-back-button"
            >
              <img src={arrowLeft} alt="Back" />
            </button>
          )}
          <div>
            <h2 className={styles.collapsedBrandName}>{brand.name}</h2>
          </div>
        </div>
      )}

      <div className={styles.header}>
        {isRTL ? (
          <>
            <div className={`${styles.languageSwitcherContainer} ${styles.tabletOnly}`}>
              <LanguageSwitcher />
            </div>

            <SearchInput
              handleSearchChange={handleSearchChange}
              searchTerm={searchTerm}
              searchPlaceholder={searchPlaceholder}
            />

            {!isCollapsed && (
              <>
                <HeaderLogo url={brand.logoUris['200_200']} alt={`${brand.name} Logo`} mobileOnly />
                <div className={`${styles.languageSwitcherContainer} ${styles.mobileOnly}`}>
                  <LanguageSwitcher />
                </div>

                {hasOrderReference && (
                  <button
                    className={`${styles.backButton} ${isCollapsed ? styles.hidden : ''}`}
                    onClick={handleBackClick}
                    aria-label={t('brandHeader.goBack')}
                    data-testid="brand-back-button"
                  >
                    <img src={chevronBack} alt="Back" />
                  </button>
                )}

                <HeaderLogo url={brand.logoUris['200_200']} alt={`${brand.name} Logo`} tabletOnly />
              </>
            )}
          </>
        ) : (
          <>
            {!isCollapsed && (
              <>
                <HeaderLogo url={brand.logoUris['200_200']} alt={`${brand.name} Logo`} />

                {hasOrderReference && (
                  <button
                    className={`${styles.backButton} ${isCollapsed ? styles.hidden : ''}`}
                    onClick={handleBackClick}
                    aria-label={t('brandHeader.goBack')}
                    data-testid="brand-back-button"
                  >
                    <img src={chevronBack} alt={t('brandHeader.back')} />
                  </button>
                )}

                {/* for mobile only */}
                <div className={`${styles.languageSwitcherContainer} ${styles.mobileOnly}`}>
                  <LanguageSwitcher />
                </div>
              </>
            )}

            <SearchInput
              handleSearchChange={handleSearchChange}
              searchTerm={searchTerm}
              searchPlaceholder={searchPlaceholder}
            />

            {/* for tablet and larger only */}
            {!isCollapsed && (
              <div className={`${styles.languageSwitcherContainer} ${styles.tabletOnly}`}>
                <LanguageSwitcher />
              </div>
            )}
          </>
        )}
      </div>

      <div className={`${styles.logoContainer} ${isCollapsed ? styles.hidden : ''}`}>
        {isValidLogoUrl(brand.logoUris['200_200']) && (
          <img
            src={brand.logoUris['200_200']}
            alt={`${brand.name} Logo`}
            className={styles.logo}
            data-testid="brand-logo"
          />
        )}
      </div>

      <div className={`${styles.brandInfo} ${isCollapsed ? styles.hidden : ''}`}>
        <div className={styles.brandDetails}>
          <div className={styles.nameHeader}>
            <h1 className={styles.brandName} data-testid="brand-name">
              {brand.name}
            </h1>
            <div className={styles.viewSwitcherWrapper} data-testid="brand-view-switcher">
              <ViewSwitcher />
            </div>
          </div>

          <div className={styles.ratingContainer}>
            <div className={styles.rating}>
              <div className={styles.stars}>
                <img src={star} alt={t('brandHeader.star')} />
              </div>
              <span className={styles.ratingText} data-testid="brand-rating-text">
                {brand.rating.value} ({roundRatingsNumber(brand.rating.votes)})
              </span>
            </div>

            {brand.description && (
              <>
                <span
                  className={`${styles.cuisineType} ${isDescriptionShowed && styles.descriptionShowed}`}
                  data-testid="brand-description"
                >
                  {brand.description}
                </span>
                {brand.description.length > 230 && (
                  <span
                    className={styles.seeMore}
                    onClick={() => setIsDescriptionShowed(!isDescriptionShowed)}
                    data-testid="brand-see-more"
                  >
                    {isDescriptionShowed ? t('brandHeader.seeLess') : t('brandHeader.seeMore')}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
